type AccountBalance = {
  address: string;
  balance: bigint;
  valueUsd: bigint;
};

export const privateAccountQueryAddress = (
  subwallet: string,
  _v2Enabled: boolean
): string => subwallet;

export const privateAccountMetadataAddresses = (
  proxyContract: string | undefined,
  v2Enabled: boolean
): string[] => (v2Enabled && proxyContract ? [proxyContract] : []);

/**
 * Keep public and private account identifiers out of the same account-data
 * request. While Private Mode is restoring, fail closed instead of briefly
 * falling back to the connected public wallet.
 */
export const accountDataQueryScope = ({
  selectedAddress,
  publicMetadataAddresses,
  isPrivateMode,
  subWalletAddress,
  proxyContract,
  v2Enabled,
}: {
  selectedAddress: string | undefined;
  publicMetadataAddresses: readonly string[];
  isPrivateMode: boolean;
  subWalletAddress: string | null | undefined;
  proxyContract: string | undefined;
  v2Enabled: boolean;
}): { accountAddress: string | undefined; queryAddresses: string[] } => {
  if (isPrivateMode) {
    return {
      accountAddress: subWalletAddress
        ? privateAccountQueryAddress(subWalletAddress, v2Enabled)
        : undefined,
      queryAddresses: privateAccountMetadataAddresses(proxyContract, v2Enabled),
    };
  }

  return {
    accountAddress: selectedAddress,
    queryAddresses: [...publicMetadataAddresses],
  };
};

/**
 * The public V2 proxy may be queried for asset metadata, but its aggregate
 * reserves never belong to the current user. Recompute both totals solely
 * from retained accounts so proxy funds cannot be displayed as user funds.
 */
export const excludeMetadataAccount = <T extends AccountBalance>(
  accounts: readonly T[],
  metadataAddress: string | undefined
): { accounts: T[]; balance: bigint; valueUsd: bigint } => {
  const retained = metadataAddress
    ? accounts.filter((account) => account.address !== metadataAddress)
    : [...accounts];
  return {
    accounts: retained,
    balance: retained.reduce((total, account) => total + account.balance, 0n),
    valueUsd: retained.reduce((total, account) => total + account.valueUsd, 0n),
  };
};
